import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FaTimes, FaUser, FaEnvelope, FaLock, FaUserTag, FaEye, FaEyeSlash, FaPhone, FaCalendarAlt, FaMapMarkerAlt, FaProjectDiagram } from 'react-icons/fa';
import { useAuth } from '../../provider/auth.provider';
import { SearchableSelect, SelectWithSearch, SelectWithSearch2, SelectOption } from '../../utils/select-with-search';
import { countries } from '../../utils/countries';
import axiosInstance from '../../libs/axios';
import { CloseModal } from '../sites/closemodal';
import { getAssociationDisplayLabel } from '../../utils/association.utils';

interface Role {
  id: number;
  name: string;
}

interface Project {
  id: number;
  project_name: string;
  status: string;
}

interface Department {
  id: number;
  name: string;
}

export interface CountryOption {
  value: string;
  label: string;
  flag: string;
}

interface AddUserOffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (userData: UserFormData) => void;
  roles: Role[];
  loading?: boolean;
}

// Extend with optional fields so parent prop types remain compatible
interface UserFormData {
  email: string;
  employee_code: string;
  username: string;
  first_name: string;
  last_name: string;
  password: string;
  role_id: string;
  association_type: string;
  // new optional fields sent to API if present
  phone_number?: string | null;
  country_code?: string | null;
  date_of_birth?: string | null; // ISO date (yyyy-mm-dd)
  gender?: string | null;
  address?: string | null;
  city?: string | null;
 
  state_circle_id?: number | undefined,
  district_id?: number | undefined,
  address_state_id?: number | null | undefined,
  address_district_id?: number | null | undefined,
  country?: string | null;
  pincode?: string | null;
  reporting_latitude?: string | null;
  reporting_longitude?: string | null;
  project_ids?: number[];
  department_id?: number | null;
  designation_id?: number | null;
  project_manager_id?: number | null;
  vendor_id?: number | null;
  status: 'initiated' | 'pending' | 'verified' | 'suspended' | 'deactivated';
  employment_status: 'working' | 'not_working' | 'left';
}

const STATUS_OPTIONS: UserFormData['status'][] = [
  'initiated',
  'pending',
  'verified',
  'suspended',
  'deactivated'
];

const EMPLOYMENT_STATUS_OPTIONS: UserFormData['employment_status'][] = [
  'working',
  'not_working',
  'left'
];

export const AddUserOffcanvas: React.FC<AddUserOffcanvasProps> = ({
  isOpen,
  onClose,
  onSubmit,
  roles,
  loading = false
}) => {
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    employee_code: '',
    username: '',
    first_name: '',
    last_name: '',
    password: '',
    role_id: '',
    association_type: 'inhouse',
    phone_number: '',
    country_code: '+91',
    date_of_birth: '',
    gender: '',
    address: '',
    city: '',
    state_circle_id: undefined,
    district_id: undefined,
    address_state_id: undefined,
    address_district_id: undefined,
    country: 'India',
    pincode: '',
    reporting_latitude: '',
    reporting_longitude: '',
    project_ids: [],
    department_id: null,
    vendor_id: null,
    status: 'initiated',
    employment_status: 'working'
  });

  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof UserFormData, string>>>({});
  const [selectedProjectState, setSelectedProjectState] = useState<{ id: number; label: string; value: string | number } | null>(null)
  const [selectedProjectDistrict, setSelectedProjectDistrict] = useState<{ id: number; label: string; value: string | number } | null>(null)
  const [selectedAddressState, setSelectedAddressState] = useState<{ id: number; label: string; value: string | number } | null>(null)
  const [selectedAddressDistrict, setSelectedAddressDistrict] = useState<{ id: number; label: string; value: string | number } | null>(null)
  const [selectedRole, setSelectedRole] = useState<{ value: string | number; label: string } | null>(null)
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState<{ value: string | number; label: string }[] | null>(null)
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<{ value: string | number; label: string } | null>(null);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);

  const [designations, setDesignations] = useState<{ value: string | number; label: string }[]>([]);
  const [selectedDesignation, setSelectedDesignation] = useState<{ value: string | number; label: string } | null>(null);
  const [designationLoading, setDesignationLoading] = useState(false);

  const [selectedProjectManager, setSelectedProjectManager] = useState<SelectOption | null>(null);
  const [reportingPersonDesignationIds, setReportingPersonDesignationIds] = useState<number[]>([]);
  const [reportingPersonIdsLoading, setReportingPersonIdsLoading] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [vendors, setVendors] = useState<{ value: number; label: string }[]>([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<{ value: string | number; label: string } | null>(null);
  const { hasPermission } = useAuth();

  useEffect(() => {
    if (!isOpen) {
      // Reset form when closing
      setFormData({
        email: '',
        employee_code: '',
        username: '',
        first_name: '',
        last_name: '',
        password: '',
        role_id: '',
        association_type: 'inhouse',
        phone_number: '',
        country_code: '+91',
        date_of_birth: '',
        gender: '',
        address: '',
        city: '',
        state_circle_id: undefined,
        district_id: undefined,
        address_state_id: undefined,
        address_district_id: undefined,
        country: '',
        pincode: '',
        reporting_latitude: '',
        reporting_longitude: '',
        project_ids: [],
        department_id: null,
        vendor_id: null,
        status: 'initiated',
        employment_status: 'working'
      });
      setErrors({});
      setShowPassword(false);
      setSelectedRole(null);
      setSelectedProjects(null);
      setSelectedDepartment(null);
      setSelectedDesignation(null);
      setSelectedProjectManager(null);
      setReportingPersonDesignationIds([]);
      setSelectedProjectState(null);
      setSelectedProjectDistrict(null);
      setSelectedAddressState(null);
      setSelectedAddressDistrict(null);
      setSelectedVendor(null);
    } else {
      setShowCloseModal(false);
    }
  }, [isOpen]);

  const fetchDepartments = useCallback(async (search: string = '') => {
    setDepartmentsLoading(true);
    try {
      const response = await axiosInstance.get('/departments/list', {
        params: {
          search: search.trim() || undefined,
          page: 1,
          limit: 100,
          sort_by: 'name',
          sort_order: 'ASC'
        }
      });

      if (response.data.success) {
        setDepartments(response.data.data.departments || []);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    } finally {
      setDepartmentsLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      setProjectsLoading(true);
      try {
        const response = await axiosInstance.get('/projects/list', {
          params: { page: 1, limit: 100, status: 'ACTIVE' }
        });
        if (response.data.success) {
          setProjects(response.data.data.projects || []);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setProjectsLoading(false);
      }
    };

    const fetchDesignations = async (search: string = "") => {
      setDesignationLoading(true);
      try {
        const response = await axiosInstance.get('/designations/list', {
          params: { search: search.trim() || undefined, limit: 100, status: 'active' }
        });
        if (response.data.success) {
          setDesignations(response.data.data.designations.map((d: any) => ({
            value: d.id,
            label: d.name
          })));
        }
      } catch (error) {
        console.error('Error fetching designations:', error);
      } finally {
        setDesignationLoading(false);
      }
    };

    const fetchReportingPersonDesignationIds = async () => {
      setReportingPersonIdsLoading(true);
      try {
        const response = await axiosInstance.get('/designations/reporting-person/ids');
        if (response.data.success) {
          setReportingPersonDesignationIds(response.data.data.designation_ids || []);
        }
      } catch (error) {
        console.error('Error fetching reporting person designation ids:', error);
      } finally {
        setReportingPersonIdsLoading(false);
      }
    };

    const fetchVendors = async () => {
      setVendorsLoading(true);
      try {
        const res = await axiosInstance.get('/vendor/active');
        if (res.data.success) {
          setVendors((res.data.data.vendors || []).map((v: any) => ({
            value: v.id,
            label: `${v.vendor_full_name} (${v.vendor_short_name})`
          })));
        }
      } catch { /* silent */ } finally {
        setVendorsLoading(false);
      }
    };

    if (isOpen) {
      fetchProjects();
      fetchDepartments();
      fetchDesignations();
      fetchReportingPersonDesignationIds();
      fetchVendors();
    }
  }, [isOpen]);

  const reportingDesignationIdsParam = useMemo(
    () => reportingPersonDesignationIds.join(','),
    [reportingPersonDesignationIds]
  );

  const canFetchReportingManagers = Boolean(reportingDesignationIdsParam);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof UserFormData, string>> = {};

    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';

    if (!formData.employee_code.trim()) {
      newErrors.employee_code = 'Employee code is required';
    } else if (formData.employee_code.length < 3) {
      newErrors.employee_code = 'Employee code must be at least 3 characters';
    }
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.role_id) newErrors.role_id = 'Role is required';

    if (!formData.status || !STATUS_OPTIONS.includes(formData.status)) {
      newErrors.status = 'Select a valid status';
    }

    if (!formData.employment_status || !EMPLOYMENT_STATUS_OPTIONS.includes(formData.employment_status)) {
      newErrors.employment_status = 'Select a valid employment status';
    }

    if (!formData.state_circle_id) {
      newErrors.state_circle_id = 'Project state is required';
    }

    if (!formData.district_id) {
      newErrors.district_id = 'Project district is required';
    }

    if (formData.association_type === 'vendor' && !formData.vendor_id) {
      newErrors.vendor_id = 'Vendor is required for vendor association';
    }

    const hasAddressState = formData.address_state_id !== undefined && formData.address_state_id !== null;
    const hasAddressDistrict = formData.address_district_id !== undefined && formData.address_district_id !== null;
    if (hasAddressState !== hasAddressDistrict) {
      newErrors.address_state_id = 'Select address state and district';
      newErrors.address_district_id = 'Select address state and district';
    }

    // Optional validations
    if (formData.phone_number && !/^\d{7,15}$/.test(formData.phone_number)) {
      newErrors.phone_number = 'Phone must be 7-15 digits';
    }

    // if (formData.country_code && !/^\+?\d{1,4}$/.test(formData.country_code)) {
    //   newErrors.country_code = 'Country code must be like +1, 91';
    // }

    if (formData.pincode && !/^\d{3,10}$/.test(formData.pincode)) {
      newErrors.pincode = 'Pincode must be 3-10 digits';
    }

    if (!formData.reporting_latitude) {
      newErrors.reporting_latitude = 'Latitude is required';
    } else {
      const lat = Number(formData.reporting_latitude);
      if (!Number.isFinite(lat) || lat < 6 || lat > 38) {
        newErrors.reporting_latitude = 'Latitude must be within India range (6 to 38)';
      }
    }
    if (!formData.reporting_longitude) {
      newErrors.reporting_longitude = 'Longitude is required';
    } else {
      const lng = Number(formData.reporting_longitude);
      if (!Number.isFinite(lng) || lng < 68 || lng > 98) {
        newErrors.reporting_longitude = 'Longitude must be within India range (68 to 98)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleInputChange = (field: keyof UserFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const countryOptions: CountryOption[] = countries.map(country => ({
    value: country.code,
    label: `${country.flag} ${country.code}`,
    flag: country.flag
  }));

  // const updateCountryCode = (option: CountryOption | null) => {
  //   setFormData(prev => ({ ...prev, country_code: option ? option.value : undefined }));
  // };

  const today = new Date();
  const eighteenYearsAgo = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate()
  ).toISOString().split("T")[0];

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !loading) {
        setShowCloseModal(true);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, loading]);

  const requestClose = () => {
    if (!loading) {
      setShowCloseModal(true);
    }
  };

  const confirmClose = () => {
    setShowCloseModal(false);
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 transition-opacity z-50 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        onClick={requestClose}
      />

      {/* Offcanvas */}
      <div
        className={`overflow-y-auto fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl transform transition-transform duration-300 ease-in-out z-50 ${isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Add New User</h2>
            <p className="text-sm text-gray-600 mt-1">Create a new user account</p>
          </div>
          <button
            onClick={requestClose}
            className="p-2 hover:bg-gray-200 rounded transition-colors"
            disabled={loading}
          >
            <FaTimes className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-6 space-y-6" autoComplete="off">

            {/* Personal Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center gap-2">
                <FaUser className="w-4 h-4 text-blue-900" />
                Personal Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => handleInputChange('first_name', e.target.value)}
                    className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-colors ${errors.first_name ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    placeholder="Enter first name"
                    disabled={loading}
                  />
                  {errors.first_name && <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => handleInputChange('last_name', e.target.value)}
                    className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-colors ${errors.last_name ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    placeholder="Enter last name"
                    disabled={loading}
                  />
                  {errors.last_name && <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>}
                </div>
              </div>
            </div>

            {/* Account Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center gap-2">
                <FaEnvelope className="w-4 h-4 text-blue-900" />
                Account Information
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Employee Code *</label>
                  <input
                    type="text"
                    value={formData.employee_code}
                    onChange={(e) => handleInputChange('employee_code', e.target.value)}
                    className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-colors ${errors.employee_code ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    placeholder="Enter employee code"
                    disabled={loading}
                  />
                  {errors.employee_code && <p className="mt-1 text-sm text-red-600">{errors.employee_code}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Username *</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    autoComplete="off"
                    className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-colors ${errors.username ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    placeholder="Enter username"
                    disabled={loading}
                  />
                  {errors.username && <p className="mt-1 text-sm text-red-600">{errors.username}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    autoComplete="off"
                    className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-colors ${errors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    placeholder="Enter email address"
                    disabled={loading}
                  />
                  {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Password *</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      autoComplete="new-password"
                      className={`w-full px-4 py-2 pr-10 border rounded focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-colors ${errors.password ? 'border-red-500 bg-red-50' : 'border-gray-300'
                        }`}
                      placeholder="Enter password"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <FaEyeSlash className="w-4 h-4" /> : <FaEye className="w-4 h-4" />}
                    </button>
                  </div>
                  {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center gap-2">
                <FaPhone className="w-4 h-4 text-blue-900" />
                Contact Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {/* <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Country Code</label>
                  <SearchableSelect
                    value={countryOptions.find(c => c.value === formData.country_code) || null}
                    onChange={(option: any) => updateCountryCode(option)}
                    options={countryOptions}
                    placeholder="Code"
                    disabled={loading}
                  />
                  {errors.country_code && <p className="mt-1 text-sm text-red-600">{errors.country_code}</p>}
                </div> */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                  <input
                    type="text"
                    value={formData.phone_number || ''}
                    onChange={(e) => handleInputChange('phone_number', e.target.value)}
                    className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-colors ${errors.phone_number ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    placeholder="Enter phone number"
                    disabled={loading}
                  />
                  {errors.phone_number && <p className="mt-1 text-sm text-red-600">{errors.phone_number}</p>}
                </div>
              </div>
            </div>

            {/* Profile Details */}
            <div>
              <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center gap-2">
                <FaCalendarAlt className="w-4 h-4 text-blue-900" />
                Profile Details
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
                  <input
                    type="date"
                    value={formData.date_of_birth || ''}
                    onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                    className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-colors border-gray-300"
                    disabled={loading}
                    max={eighteenYearsAgo}
                  />

                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                  <select
                    value={formData.gender || ''}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                    className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-colors border-gray-300"
                    disabled={loading}
                  >
                    <option value="">Select</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Address Details */}
            <div>
              <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center gap-2">
                <FaMapMarkerAlt className="w-4 h-4 text-blue-900" />
                Address Details
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                  <input
                    type="text"
                    value={formData.address || ''}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-colors border-gray-300"
                    placeholder="Street address"
                    disabled={loading}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                    <SelectWithSearch
                      value={selectedAddressState}
                      onChange={(option) => {
                        setSelectedAddressState(option)
                        setSelectedAddressDistrict(null)
                        setFormData(prev => ({ ...prev, address_state_id: option?.id, address_district_id: undefined }))
                      }}
                      placeholder="Select state"
                      apiEndpoint="/states/list"
                      dataKey="states"
                      labelKey="name"
                      displayFormat={(item) => `${item.name}`}
                    />
                    {errors.address_state_id && <p className="mt-1 text-sm text-red-600">{errors.address_state_id}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">District</label>
                    <SelectWithSearch
                      value={selectedAddressDistrict}
                      onChange={(option) => {
                        setSelectedAddressDistrict(option)
                        setFormData(prev => ({ ...prev, address_district_id: option?.id }))
                      }}
                      placeholder="Select district"
                      disabled={!selectedAddressState}
                      apiEndpoint="/states"
                      dataKey="districts"
                      dependencyId={selectedAddressState?.id}
                      labelKey="name"
                    />
                    {errors.address_district_id && <p className="mt-1 text-sm text-red-600">{errors.address_district_id}</p>}
                  </div>
                  {/* <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                    <input
                      type="text"
                      value={formData.city || ''}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-colors border-gray-300"
                      disabled={loading}
                    />
                  </div> */}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {/* <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                    <input
                      type="text"
                      value={formData.country || ''}
                      onChange={(e) => handleInputChange('country', e.target.value)}
                      className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-colors border-gray-300"
                      disabled={loading}
                    />
                  </div> */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Pincode</label>
                    <input
                      type="text"
                      value={formData.pincode || ''}
                      onChange={(e) => handleInputChange('pincode', e.target.value)}
                      className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-colors ${errors.pincode ? 'border-red-500 bg-red-50' : 'border-gray-300'
                        }`}
                      disabled={loading}
                    />
                    {errors.pincode && <p className="mt-1 text-sm text-red-600">{errors.pincode}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Reporting Latitude *</label>
                    <input
                      type="text"
                      value={formData.reporting_latitude || ''}
                      onChange={(e) => handleInputChange('reporting_latitude', e.target.value)}
                      className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-colors ${errors.reporting_latitude ? 'border-red-500 bg-red-50' : 'border-gray-300'
                        }`}
                      placeholder="Latitude"
                      disabled={loading}
                    />
                    {errors.reporting_latitude && <p className="mt-1 text-sm text-red-600">{errors.reporting_latitude}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Reporting Longitude *</label>
                    <input
                      type="text"
                      value={formData.reporting_longitude || ''}
                      onChange={(e) => handleInputChange('reporting_longitude', e.target.value)}
                      className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-colors ${errors.reporting_longitude ? 'border-red-500 bg-red-50' : 'border-gray-300'
                        }`}
                      placeholder="Longitude"
                      disabled={loading}
                    />
                    {errors.reporting_longitude && <p className="mt-1 text-sm text-red-600">{errors.reporting_longitude}</p>}
                  </div>
                </div>
              </div>
            </div>

            <div>
               <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center gap-2">
                 <FaProjectDiagram className="w-4 h-4 text-blue-900" />
                 Work Information
               </h3>
               <div className="grid grid-cols-2 gap-4">
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-2">Project Circle</label>
                   <SelectWithSearch
                     value={selectedProjectState}
                     onChange={(option) => {
                       setSelectedProjectState(option)
                       setSelectedProjectDistrict(null)
                       setFormData(prev => ({ ...prev, state_circle_id: option?.id, district_id: undefined }))
                     }}
                     placeholder="Select project state"
                     apiEndpoint="/state-circle/list"
                     dataKey="states"
                     labelKey="state_name"
                     displayFormat={(item) => `${item.state_name}`}
                    //  extraParams={}
                   />
                   {errors.state_circle_id && <p className="mt-1 text-sm text-red-600">{errors.state_circle_id}</p>}
                 </div>
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-2">District Circle</label>
                   <SelectWithSearch
                     value={selectedProjectDistrict}
                     onChange={(option) => {
                       setSelectedProjectDistrict(option)
                       setFormData(prev => ({ ...prev, district_id: option?.id }))
                     }}
                     placeholder="Select project district"
                     disabled={!selectedProjectState}
                     apiEndpoint="/state-circle"
                     dataKey="districts"
                     dependencyId={selectedProjectState?.id}
                     labelKey="name"
                   />
                   {errors.district_id && <p className="mt-1 text-sm text-red-600">{errors.district_id}</p>}
                 </div>
                 <div className="col-span-2">
                   <label className="block text-sm font-medium text-gray-700 mb-2">Assign Projects</label>
                   <SearchableSelect
                     value={selectedProjects}
                     isMulti={true}
                    showSelectAllVisible={true}
                    onChange={(options: any) => {
                      setSelectedProjects(options);
                      setSelectedProjectManager(null);
                      setFormData(prev => ({
                        ...prev,
                        project_ids: options ? options.map((opt: any) => Number(opt.value)) : [],
                        project_manager_id: null
                      }));
                    }}
                    options={projects.map((project) => ({
                      value: project.id,
                      label: project.project_name
                    }))}
                    placeholder="Search and select projects"
                    disabled={loading}
                    isLoading={projectsLoading}
                    minSearchLength={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Department/Project Group</label>
                  <SearchableSelect
                    value={selectedDepartment}
                    onChange={(option) => {
                      setSelectedDepartment(option);
                      setFormData(prev => ({ ...prev, department_id: option ? Number(option.value) : null }));
                    }}
                    options={departments.map((department) => ({
                      value: department.id,
                      label: department.name
                    }))}
                    placeholder="Search and select department"
                    disabled={loading}
                    isLoading={departmentsLoading}
                    onSearch={fetchDepartments}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Designation</label>
                  <SearchableSelect
                    value={selectedDesignation}
                    onChange={(option) => {
                      setSelectedDesignation(option);
                      setFormData(prev => ({ ...prev, designation_id: option ? Number(option.value) : null }));
                    }}
                    options={designations}
                    placeholder="Search and select designation"
                    disabled={loading}
                    isLoading={designationLoading}
                    minSearchLength={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Reporting Person</label>
                  <SelectWithSearch2
                    placeholder={canFetchReportingManagers ? "Select reporting person" : "No reporting-person designations configured"}
                    value={selectedProjectManager}
                    onChange={(option) => {
                      setSelectedProjectManager(option);
                      setFormData(prev => ({ ...prev, project_manager_id: option ? Number(option.value) : null }));
                    }}
                    apiEndpoint={canFetchReportingManagers ? "/users/assignment/users" : undefined}
                    extraParams={{
                      designation_id: reportingDesignationIdsParam
                    }}
                    mapOption={(user: any) => ({
                      id: user.id,
                      value: user.id,
                      label: `${user.first_name} ${user.last_name} (${user.username}) - ${user?.employee_code || 'N/A'}`,
                    })}
                    className="w-full"
                    disabled={loading || reportingPersonIdsLoading || !canFetchReportingManagers}
                  />
                </div>
              </div>
            </div>

            {/* Role and Status */}
            <div>
              <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center gap-2">
                <FaUserTag className="w-4 h-4 text-blue-900" />
                Role & Status
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">User Role *</label>
                  <SearchableSelect
                    value={selectedRole}
                    onChange={(option) => {
                      setSelectedRole(option);
                      handleInputChange('role_id', option ? String(option.value) : '');
                    }}
                    options={roles.map((role) => ({
                      value: role.id,
                      label: role.name
                    }))}
                    placeholder="Select a role"
                    disabled={loading}
                    error={errors.role_id}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status *</label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value as UserFormData['status'])}
                    className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-colors ${errors.status ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    disabled={loading}
                  >
                    {STATUS_OPTIONS.map(s => (
                      <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                    ))}
                  </select>
                  {errors.status && <p className="mt-1 text-sm text-red-600">{errors.status}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Association Type</label>
                  <select
                    value={formData.association_type || 'inhouse'}
                    onChange={(e) => {
                      handleInputChange('association_type', e.target.value);
                      if (e.target.value !== 'vendor') {
                        setSelectedVendor(null);
                        setFormData(prev => ({ ...prev, vendor_id: null }));
                      }
                    }}
                    className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-colors border-gray-300"
                    disabled={loading}
                  >
                    <option value="inhouse">{getAssociationDisplayLabel('inhouse')}</option>
                    <option value="vendor">{getAssociationDisplayLabel('vendor')}</option>
                  </select>
                </div>
                {formData.association_type === 'vendor' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Vendor <span className="text-red-500">*</span></label>
                    <SearchableSelect
                      value={selectedVendor}
                      onChange={(option) => {
                        setSelectedVendor(option);
                        setFormData(prev => ({ ...prev, vendor_id: option ? Number(option.value) : null }));
                        if (errors.vendor_id) setErrors(prev => ({ ...prev, vendor_id: undefined }));
                      }}
                      options={vendors}
                      placeholder="Select vendor"
                      disabled={loading}
                      isLoading={vendorsLoading}
                      error={errors.vendor_id}
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Employment Status *</label>
                  <select
                    value={formData.employment_status}
                    onChange={(e) => handleInputChange('employment_status', e.target.value as UserFormData['employment_status'])}
                    className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-colors ${errors.employment_status ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    disabled={loading}
                  >
                    {EMPLOYMENT_STATUS_OPTIONS.map(s => (
                      <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1).replace('_', ' ')}</option>
                    ))}
                  </select>
                  {errors.employment_status && <p className="mt-1 text-sm text-red-600">{errors.employment_status}</p>}
                </div>
              </div>
            </div>

          </form>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 bg-gray-50">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={requestClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors font-medium"
              disabled={loading}
            >
              Cancel
            </button>

            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 bg-blue-900 hover:bg-blue-800 disabled:bg-blue-400 text-white px-4 py-2 rounded transition-colors font-medium flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Creating...
                </>
              ) : (
                'Create User'
              )}
            </button>
          </div>
        </div>
      </div>
      <CloseModal
        isOpen={showCloseModal}
        onClose={() => setShowCloseModal(false)}
        onConfirm={confirmClose}
      />
    </>
  );
};
